import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import { apiClient } from './api';

export interface Participant {
  id: number;
  username: string;
  full_name: string;
  email: string;
  profile_picture?: string | null;
  prof_pics?: string | null;
  google_prof_pics?: string | null;
  is_online?: boolean;
}

export interface CardPayload {
  title?: string;
  requested_change?: string;
  proposed_terms?: string;
  offer_amount?: number | string;
  currency?: string;
  status?: string;
  [key: string]: any;
}

export interface ChatAttachment {
  id?: string;
  name?: string;
  original_filename?: string;
  file_url?: string;
  url?: string;
  size?: string;
  resource_type?: string;
  public_id?: string;
  mime_type?: string;
  file_size?: number;
}

export interface ChatMessage {
  id: string;
  conversation_id: string;
  sender_id: number;
  sender_name?: string;
  sender_avatar?: string | null;
  kind?: 'text' | 'card' | 'system' | string;
  body: string;
  body_html?: string;
  card_type?: string | null;
  card_payload?: CardPayload | null;
  deleted?: boolean;
  deleted_at?: string | null;
  created_at: string;
  attachments?: ChatAttachment[];
  is_read?: boolean;
  read?: boolean;
  read_at?: string | null;
}

export interface ConversationItem {
  id?: string;
  conversation_uuid: string;
  title?: string;
  name?: string;
  context?: {
    type: string;
    id: string;
    title: string;
  };
  participants?: Participant[];
  other_participant?: Participant;
  last_message?: ChatMessage | null;
  last_message_text?: string;
  last_message_time?: string;
  last_message_at?: string | null;
  unread_count?: number;
  is_muted?: boolean;
  muted?: boolean;
  created_at: string;
  updated_at: string;
}

export interface ConversationListResponse {
  success?: boolean;
  message?: string;
  data?:
    | {
        count: number;
        results: ConversationItem[];
      }
    | ConversationItem[];
  results?: ConversationItem[];
}

export interface ConversationDetailResponse {
  success?: boolean;
  data: ConversationItem;
}

export interface MessageListResponse {
  success?: boolean;
  data?:
    | {
        count: number;
        results: ChatMessage[];
      }
    | ChatMessage[];
  results?: ChatMessage[];
}

export interface OpenConversationRequest {
  recipient_id?: number;
  context_type?: string;
  context_id?: string;
  participant_ids?: number[];
  recipient_email?: string;
  email?: string;
  service_application_uuid?: string;
  gig_application_uuid?: string;
  title?: string;
  initial_message?: string;
}

export interface SendMessageRequest {
  body: string;
  message_html?: string;
  body_html?: string;
  card_type?: string | null;
  card_payload?: CardPayload | null;
  attachments?: any[];
}

// ── Helpers ──────────────────────────────────────────────────────────────────

export function normalizeConversation(item: any): ConversationItem {
  if (!item) return item;
  return {
    ...item,
    conversation_uuid: item.conversation_uuid || item.id || '',
    is_muted: item.is_muted ?? item.muted ?? false,
    updated_at: item.updated_at || item.last_message_at || item.created_at,
    last_message_time:
      item.last_message_time || item.last_message_at || item.updated_at || item.created_at,
  };
}

// ── Queries & Mutations ────────────────────────────────────────────────────────

/** List all conversations in the user's inbox */
export const useGetConversations = (options?: { refetchInterval?: number | false }) => {
  return useQuery({
    queryKey: ['conversations'],
    queryFn: async () => {
      const { data } = await apiClient.get<any>('/conversations/');
      // Normalize different backend response shapes
      let rawList: any[] = [];
      if (Array.isArray(data)) {
        rawList = data;
      } else if (Array.isArray(data?.results)) {
        rawList = data.results;
      } else if (Array.isArray(data?.data?.results)) {
        rawList = data.data.results;
      } else if (Array.isArray(data?.data)) {
        rawList = data.data;
      }
      return rawList.map(normalizeConversation);
    },
    staleTime: Infinity, // WebSocket keeps data fresh — no automatic refetches
    refetchOnWindowFocus: false,
    ...options,
  });
};

/** Get conversation detail */
export const useGetConversationDetail = (conversationUuid?: string) => {
  return useQuery({
    queryKey: ['conversation', conversationUuid],
    enabled: Boolean(conversationUuid),
    queryFn: async () => {
      const { data } = await apiClient.get<any>(`/conversations/${conversationUuid}/`);
      return normalizeConversation(data?.data || data);
    },
  });
};

const DEFAULT_CHAT_PAGE_SIZE = 10;

/** List messages for a specific conversation (cursor-paginated infinite query) */
export const useGetConversationMessages = (conversationUuid?: string, options?: any) => {
  return useInfiniteQuery({
    queryKey: ['conversation-messages', conversationUuid],
    enabled: Boolean(conversationUuid),
    initialPageParam: null as string | null,
    queryFn: async ({ pageParam }) => {
      let url = `/conversations/${conversationUuid}/messages/?limit=${DEFAULT_CHAT_PAGE_SIZE}`;
      if (pageParam) {
        url += `&before=${pageParam}`;
      }
      const { data } = await apiClient.get<any>(url);

      const msgs = Array.isArray(data)
        ? data
        : Array.isArray(data?.data)
          ? data.data
          : Array.isArray(data?.results)
            ? data.results
            : Array.isArray(data?.data?.results)
              ? data.data.results
              : [];

      return {
        raw: data,
        messages: msgs,
        _originalCount: msgs.length,
      };
    },
    getNextPageParam: (lastPage: any) => {
      if (!lastPage || lastPage._originalCount < DEFAULT_CHAT_PAGE_SIZE) {
        return undefined;
      }
      const msgs = lastPage.messages || [];
      const oldestMsg = msgs[msgs.length - 1];
      return oldestMsg?.id || undefined;
    },
    ...options,
  });
};

/** Open (find-or-create) conversation */
export const useOpenConversation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (payload: OpenConversationRequest) => {
      const { data } = await apiClient.post<any>('/conversations/', payload);
      return normalizeConversation(data?.data || data);
    },
    onSuccess: (newConv) => {
      queryClient.invalidateQueries({ queryKey: ['conversations'] });
      if (newConv?.conversation_uuid) {
        queryClient.invalidateQueries({ queryKey: ['conversation', newConv.conversation_uuid] });
      }
    },
  });
};

/** Send a message inside a conversation */
export const useSendMessage = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      conversationUuid,
      payload,
    }: {
      conversationUuid: string;
      payload: SendMessageRequest;
    }) => {
      const { data } = await apiClient.post<any>(
        `/conversations/${conversationUuid}/messages/`,
        payload,
      );
      return (data?.data || data) as ChatMessage;
    },
    onSuccess: (newMsg, variables) => {
      // Optimistically append to messages cache (WS echo will deduplicate)
      if (newMsg?.id) {
        queryClient.setQueryData<any>(
          ['conversation-messages', variables.conversationUuid],
          (old: any) => {
            if (!old) return old;
            const hasMessage = old.pages?.some((page: any) => {
              const results = Array.isArray(page) ? page : page?.data || page?.results || [];
              return results.some((m: any) => m.id === newMsg.id);
            });
            if (hasMessage) return old;

            const newPages = [...old.pages];
            if (newPages.length > 0) {
              const firstPage = newPages[0];
              if (Array.isArray(firstPage)) {
                newPages[0] = [newMsg, ...firstPage];
              } else if (firstPage && Array.isArray(firstPage.data)) {
                newPages[0] = {
                  ...firstPage,
                  data: [newMsg, ...firstPage.data],
                };
              } else if (firstPage && Array.isArray(firstPage.results)) {
                newPages[0] = {
                  ...firstPage,
                  results: [newMsg, ...firstPage.results],
                };
              }
            } else {
              newPages[0] = {
                data: [newMsg],
                next: null,
                previous: null,
              };
            }

            return {
              ...old,
              pages: newPages,
            };
          },
        );
      }
    },
  });
};

/** Mute / unmute conversation */
export const useMuteConversation = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({
      conversationUuid,
      isMuted,
    }: {
      conversationUuid: string;
      isMuted: boolean;
    }) => {
      const { data } = await apiClient.patch<any>(`/conversations/${conversationUuid}/mute/`, {
        muted: isMuted,
        is_muted: isMuted,
      });
      return (data?.data || data) as ConversationItem;
    },
    onMutate: async (variables) => {
      // Optimistically update mute state in cache
      queryClient.setQueryData<ConversationItem[]>(['conversations'], (old = []) => {
        return old.map((c) =>
          c.conversation_uuid === variables.conversationUuid
            ? { ...c, is_muted: variables.isMuted }
            : c,
        );
      });
      queryClient.setQueryData<any>(['conversation', variables.conversationUuid], (old: any) => {
        return old ? { ...old, is_muted: variables.isMuted } : old;
      });
    },
  });
};

/** Mark conversation as read */
export const useMarkConversationRead = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (conversationUuid: string) => {
      const { data } = await apiClient.post<any>(`/conversations/${conversationUuid}/read/`, {});
      return data?.data || data;
    },
    onMutate: async (conversationUuid: string) => {
      await queryClient.cancelQueries({ queryKey: ['conversations'] });
      await queryClient.cancelQueries({ queryKey: ['conversation', conversationUuid] });

      // Save previous state for rollback
      const previousConversations = queryClient.getQueryData(['conversations']);
      const previousConversation = queryClient.getQueryData(['conversation', conversationUuid]);

      // Optimistically clear unread_count
      queryClient.setQueryData<ConversationItem[]>(['conversations'], (old = []) => {
        return old.map((c) =>
          c.conversation_uuid === conversationUuid ? { ...c, unread_count: 0 } : c,
        );
      });
      queryClient.setQueryData<any>(['conversation', conversationUuid], (old: any) => {
        return old ? { ...old, unread_count: 0 } : old;
      });

      return { previousConversations, previousConversation };
    },
    onError: (_err, conversationUuid, context) => {
      // Rollback on failure
      if (context?.previousConversations) {
        queryClient.setQueryData(['conversations'], context.previousConversations);
      }
      if (context?.previousConversation) {
        queryClient.setQueryData(['conversation', conversationUuid], context.previousConversation);
      }
    },
  });
};
